class SellConsoleForm extends HTMLElement {
  static CART_STORAGE_KEY = 'rfrb-sell-cart-v1';

  connectedCallback() {
    this.apiUrl = this.dataset.apiUrl || '';
    this.catalogUrl = this.dataset.catalogUrl || '';
    this.shopSearchUrl = this.dataset.shopSearchUrl || '/search/suggest.json';
    this.state = {
      step: 1,
      category: '',
      platform: '',
      modelId: '',
      manualModel: '',
      manualEntry: false,
      fromSearch: false,
      shopProduct: null,
      storageId: '',
      controllers: 1,
      controllersDefective: 0,
      m2ExtraTb: 0,
      grade: '',
      defectIds: [],
      items: [],
      catalog: null,
    };
    this.maxItems = 100;
    this.searchTimer = null;
    this.searchAbort = null;

    this.wizard = this.querySelector('[data-sell-wizard]');
    this.statusEl = this.querySelector('[data-sell-status]');
    this.receiptEl = this.querySelector('[data-sell-receipt]');
    this.form = this.querySelector('[data-sell-contact-form]');

    this.bindNav();
    this.bindContactForm();
    this.bindCart();
    this.restoreCart();
    this.loadCatalog();
    this.renderCart();
    this.updateQuotePreview();
  }

  persistCart() {
    try {
      localStorage.setItem(SellConsoleForm.CART_STORAGE_KEY, JSON.stringify(this.state.items));
    } catch (_) {
      /* quota / private mode */
    }
  }

  clearPersistedCart() {
    try {
      localStorage.removeItem(SellConsoleForm.CART_STORAGE_KEY);
    } catch (_) {
      /* ignore */
    }
  }

  restoreCart() {
    try {
      const raw = localStorage.getItem(SellConsoleForm.CART_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      this.state.items = parsed
        .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
        .slice(0, this.maxItems)
        .map((item) => ({
          modelId: item.modelId || '',
          manualModel: item.manualModel || '',
          category: item.category || '',
          shopTitle: item.shopTitle || '',
          shopHandle: item.shopHandle || '',
          platform: item.platform || '',
          storageId: item.storageId || '',
          controllers: Number(item.controllers) || 1,
          controllersDefective: Number(item.controllersDefective) || 0,
          m2ExtraTb: Number(item.m2ExtraTb) || 0,
          grade: item.grade || '',
          defectIds: Array.isArray(item.defectIds) ? item.defectIds.map(String) : [],
          label: item.label || item.shopTitle || item.manualModel || 'Produkt',
          offeredPrice:
            item.offeredPrice == null || item.offeredPrice === ''
              ? null
              : Number(item.offeredPrice),
          manual: Boolean(item.manual),
          quantity: this.clampQty(item.quantity, this.maxItems),
        }));
      this.trimCartToMax();
    } catch (_) {
      this.state.items = [];
    }
  }

  bindCart() {
    this.querySelectorAll('[data-sell-add-item]').forEach((btn) => {
      btn.addEventListener('click', () => this.addCurrentItemToCart({ goToStep1: true }));
    });
    this.querySelectorAll('[data-sell-goto-application]').forEach((btn) => {
      btn.addEventListener('click', () => this.goToApplication());
    });
    const cart = this.querySelector('[data-sell-cart]');
    if (cart) {
      cart.addEventListener('click', (event) => {
        const removeBtn = event.target.closest('[data-sell-remove-item]');
        if (removeBtn) {
          const index = Number(removeBtn.dataset.sellRemoveItem);
          if (!Number.isFinite(index)) return;
          this.state.items.splice(index, 1);
          this.persistCart();
          this.renderCart();
          this.updateQuotePreview();
          return;
        }
        const minusBtn = event.target.closest('[data-sell-qty-minus]');
        if (minusBtn) {
          this.nudgeItemQty(Number(minusBtn.dataset.sellQtyMinus), -1);
          return;
        }
        const plusBtn = event.target.closest('[data-sell-qty-plus]');
        if (plusBtn) {
          this.nudgeItemQty(Number(plusBtn.dataset.sellQtyPlus), 1);
        }
      });
      cart.addEventListener('change', (event) => {
        const field = event.target.closest('[data-sell-qty]');
        if (!field) return;
        this.setItemQty(Number(field.dataset.sellQty), field.value);
      });
    }
  }

  clampQty(value, max = this.maxItems) {
    const n = Math.floor(Number(value));
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(Math.max(1, max), n);
  }

  itemQty(item) {
    return this.clampQty(item?.quantity);
  }

  itemIdentity(item) {
    const defects = (item?.defectIds || []).map(String).sort().join(',');
    return [
      item?.modelId || '',
      item?.manualModel || '',
      item?.shopHandle || '',
      item?.shopTitle || '',
      item?.category || '',
      item?.platform || '',
      item?.storageId || '',
      item?.grade || '',
      String(item?.controllers ?? ''),
      String(item?.controllersDefective ?? ''),
      String(item?.m2ExtraTb ?? ''),
      defects,
    ].join('\u001f');
  }

  totalUnits(exceptIndex = -1) {
    return this.state.items.reduce((sum, item, index) => {
      if (index === exceptIndex) return sum;
      return sum + this.itemQty(item);
    }, 0);
  }

  trimCartToMax() {
    let remaining = this.maxItems;
    const next = [];
    for (const item of this.state.items) {
      if (remaining < 1) break;
      const qty = Math.min(this.itemQty(item), remaining);
      next.push({ ...item, quantity: qty });
      remaining -= qty;
    }
    this.state.items = next;
  }

  maxQtyForIndex(index) {
    return Math.max(1, this.maxItems - this.totalUnits(index));
  }

  setItemQty(index, raw) {
    if (!Number.isInteger(index) || !this.state.items[index]) return;
    const parsed = Math.floor(Number(raw));
    if (!Number.isFinite(parsed) || parsed < 1) {
      this.state.items[index].quantity = 1;
    } else {
      this.state.items[index].quantity = Math.min(this.maxQtyForIndex(index), parsed);
    }
    this.persistCart();
    this.renderCart();
    this.updateQuotePreview();
  }

  nudgeItemQty(index, delta) {
    if (!Number.isInteger(index) || !this.state.items[index]) return;
    this.setItemQty(index, this.itemQty(this.state.items[index]) + delta);
  }

  showCartFull() {
    this.showError(
      (this.dataset.cartFull || 'Max {{count}} produkter per ansökan.').replace(
        '{{count}}',
        String(this.maxItems)
      )
    );
  }

  bindNav() {
    this.querySelectorAll('[data-sell-next]').forEach((btn) => {
      btn.addEventListener('click', () => this.goNext());
    });
    this.querySelectorAll('[data-sell-back]').forEach((btn) => {
      btn.addEventListener('click', () => this.goBack());
    });
    this.querySelectorAll('[data-category]').forEach((btn) => {
      btn.addEventListener('click', () => this.chooseCategory(btn.dataset.category));
    });
    this.querySelectorAll('[data-platform]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.state.platform = btn.dataset.platform;
        this.state.modelId = '';
        this.state.manualModel = '';
        this.state.manualEntry = false;
        this.state.fromSearch = false;
        this.state.shopProduct = null;
        this.renderModels();
        this.setStep(2);
      });
    });
    const globalSearch = this.querySelector('[data-sell-global-search]');
    if (globalSearch) {
      globalSearch.addEventListener('input', () => this.onGlobalSearch(globalSearch.value));
      globalSearch.addEventListener('focus', () => {
        if (String(globalSearch.value || '').trim().length >= 2) {
          this.onGlobalSearch(globalSearch.value);
        }
      });
    }
    const modelSearch = this.querySelector('[data-sell-search]');
    if (modelSearch) {
      modelSearch.addEventListener('input', () => this.renderModels(modelSearch.value));
    }
    const missing = this.querySelector('[data-sell-missing-model]');
    if (missing) {
      missing.addEventListener('click', () => {
        this.state.modelId = '';
        this.state.manualModel = '';
        this.state.manualEntry = true;
        this.state.fromSearch = false;
        this.showManualModel(true);
        this.setStep(this.stepAfterModel());
      });
    }
  }

  bindContactForm() {
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  chooseCategory(category) {
    this.state.category = category;
    this.state.platform = '';
    this.state.modelId = '';
    this.state.manualModel = '';
    this.state.manualEntry = false;
    this.state.fromSearch = false;
    this.state.shopProduct = null;
    this.clearGlobalSearch();
    if (category === 'game') {
      this.renderModels();
      this.setStep(2);
      return;
    }
    this.showPlatforms(true);
    this.updateStep1Heading();
  }

  showPlatforms(show) {
    const wrap = this.querySelector('[data-sell-platforms]');
    const cats = this.querySelector('[data-sell-categories]');
    const hint = this.querySelector('[data-sell-categories-hint]');
    const back = this.querySelector('[data-sell-step1-back]');
    if (wrap) wrap.hidden = !show;
    if (cats) cats.hidden = Boolean(show);
    if (hint) hint.hidden = Boolean(show);
    if (back) back.hidden = !show;
    this.updateStep1Heading();
  }

  updateStep1Heading() {
    const heading = this.querySelector('[data-sell-step="1"] h3');
    if (!heading) return;
    if (this.state.category === 'console' || this.state.category === 'accessory') {
      heading.textContent = this.dataset.choosePlatform || 'Vilken plattform har du?';
      return;
    }
    heading.textContent = this.dataset.chooseProduct || 'Vad vill du sälja?';
  }

  async loadCatalog() {
    const urls = [this.catalogUrl, this.dataset.catalogFallbackUrl].filter(
      (url, index, list) => url && list.indexOf(url) === index
    );
    for (const url of urls) {
      try {
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const catalog = data.catalog || data;
        if (catalog && Array.isArray(catalog.models)) {
          this.state.catalog = catalog;
          break;
        }
      } catch (error) {
        console.error('[sell-console] catalog load failed', url, error);
      }
    }
    if (!this.state.catalog) {
      this.showError(
        this.dataset.catalogError ||
          'Kunde inte ladda modellkatalogen. Ladda om sidan eller försök igen senare.'
      );
      return;
    }
    await this.mergeGamesCatalog();
    this.renderModels();
    this.renderGrades();
    this.renderDefects();
  }

  async mergeGamesCatalog() {
    const hasGames = (this.state.catalog?.models || []).some((m) => this.modelCategory(m) === 'game');
    if (hasGames) return;
    const url = this.dataset.gamesCatalogUrl;
    if (!url) return;
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = await response.json();
      const games = Array.isArray(data?.models) ? data.models : Array.isArray(data) ? data : [];
      if (!games.length) return;
      const byId = new Map((this.state.catalog.models || []).map((m) => [m.id, m]));
      for (const game of games) {
        if (!game?.id) continue;
        byId.set(game.id, { ...game, category: 'game' });
      }
      this.state.catalog = { ...this.state.catalog, models: [...byId.values()] };
    } catch (error) {
      console.error('[sell-console] games catalog load failed', error);
    }
  }

  offerFromUsedPrice(usedPrice) {
    const used = Number(usedPrice);
    if (!Number.isFinite(used) || used < 100) return 5;
    return Math.max(5, Math.round(0.5 * (used - 80)));
  }

  applyPayoutShipping(grossOffer) {
    const gross = Math.max(0, Math.round(Number(grossOffer) || 0));
    if (gross >= 200) {
      return { gross, shippingFee: 0, payout: gross, freeShipping: true };
    }
    const shippingFee = 49;
    return { gross, shippingFee, payout: Math.max(0, gross - shippingFee), freeShipping: false };
  }

  currentItemLabel() {
    if (this.state.shopProduct?.title) return this.state.shopProduct.title;
    if (this.state.manualModel) return this.state.manualModel;
    return this.selectedModel()?.name || 'Produkt';
  }

  snapshotCurrentItem() {
    const quote = this.calculateLocal();
    return {
      modelId: this.state.modelId,
      manualModel: this.state.manualModel,
      category: this.state.category,
      shopTitle: this.state.shopProduct?.title || '',
      shopHandle: this.state.shopProduct?.handle || '',
      platform: this.state.platform,
      storageId: this.state.storageId,
      controllers: this.state.controllers,
      controllersDefective: this.state.controllersDefective,
      m2ExtraTb: this.state.m2ExtraTb,
      grade: this.state.grade,
      defectIds: [...this.state.defectIds],
      label: this.currentItemLabel(),
      offeredPrice: quote?.manual ? null : quote?.offeredPrice ?? null,
      manual: Boolean(quote?.manual),
      quantity: 1,
    };
  }

  resetProductState() {
    this.state.category = '';
    this.state.platform = '';
    this.state.modelId = '';
    this.state.manualModel = '';
    this.state.manualEntry = false;
    this.state.fromSearch = false;
    this.state.shopProduct = null;
    this.state.storageId = '';
    this.state.controllers = 1;
    this.state.controllersDefective = 0;
    this.state.m2ExtraTb = 0;
    this.state.grade = '';
    this.state.defectIds = [];
    this.clearGlobalSearch();
    this.showManualModel(false);
    this.showPlatforms(false);
    const modelSearch = this.querySelector('[data-sell-search]');
    if (modelSearch) modelSearch.value = '';
    this.updateStep1Heading();
  }

  canAdvanceProduct() {
    if (!this.state.modelId && !this.state.manualModel && !this.state.shopProduct) {
      this.showError(this.dataset.pickModel || 'Välj en modell.');
      return false;
    }
    if (!this.state.grade) {
      this.showError(this.dataset.pickGrade || 'Välj skick.');
      return false;
    }
    if (this.state.modelId && !this.state.storageId) {
      const model = this.selectedModel();
      this.state.storageId = model?.storageOptions?.[0]?.id || 'std';
    }
    return true;
  }

  addCurrentItemToCart({ goToStep1 = true } = {}) {
    if (!this.canAdvanceProduct()) return false;
    const snap = this.snapshotCurrentItem();
    const key = this.itemIdentity(snap);
    const existingIndex = this.state.items.findIndex((item) => this.itemIdentity(item) === key);
    const remaining =
      existingIndex >= 0 ? this.maxQtyForIndex(existingIndex) - this.itemQty(this.state.items[existingIndex]) : this.maxItems - this.totalUnits();
    if (remaining < 1) {
      this.showCartFull();
      return false;
    }
    if (existingIndex >= 0) {
      this.state.items[existingIndex].quantity = this.itemQty(this.state.items[existingIndex]) + 1;
      this.state.items[existingIndex].offeredPrice = snap.offeredPrice;
      this.state.items[existingIndex].manual = snap.manual;
    } else {
      this.state.items.push(snap);
    }
    this.persistCart();
    this.resetProductState();
    this.renderCart();
    this.renderModels();
    this.renderGrades();
    this.renderDefects();
    this.updateQuotePreview();
    if (goToStep1) this.setStep(1);
    return true;
  }

  goToApplication() {
    const hasDraft =
      Boolean(this.state.modelId || this.state.manualModel || this.state.shopProduct) &&
      Boolean(this.state.grade);
    if (hasDraft) {
      if (!this.addCurrentItemToCart({ goToStep1: false })) return;
    }
    if (!this.state.items.length) {
      this.showError(this.dataset.cartEmpty || 'Lägg till minst en produkt först.');
      return;
    }
    this.setStep(6);
  }

  calculateCartTotals() {
    let gross = 0;
    let anyManual = false;
    for (const item of this.state.items) {
      const qty = this.itemQty(item);
      if (item.manual || item.offeredPrice == null) anyManual = true;
      else gross += (Number(item.offeredPrice) || 0) * qty;
    }
    const shipping = this.applyPayoutShipping(gross);
    return { ...shipping, anyManual, count: this.totalUnits() };
  }

  linePriceText(item) {
    const qty = this.itemQty(item);
    if (item.manual || item.offeredPrice == null) {
      return this.dataset.manualQuote || 'Manuell';
    }
    const unit = Number(item.offeredPrice) || 0;
    const total = unit * qty;
    if (qty > 1) {
      return (this.dataset.cartLinePrice || '{{qty}} × {{unit}} kr = {{total}} kr')
        .replace('{{qty}}', String(qty))
        .replace('{{unit}}', String(unit))
        .replace('{{total}}', String(total));
    }
    return `${unit} kr`;
  }

  renderCart() {
    const panel = this.querySelector('[data-sell-cart]');
    if (!panel) return;
    const count = this.totalUnits();
    panel.hidden = this.state.items.length === 0;
    const countEl = panel.querySelector('[data-sell-cart-count]');
    const listEl = panel.querySelector('[data-sell-cart-list]');
    const totalEl = panel.querySelector('[data-sell-cart-total]');
    const totals = this.calculateCartTotals();
    if (countEl) {
      countEl.textContent = (this.dataset.cartCount || '{{count}} produkter').replace(
        '{{count}}',
        String(count)
      );
    }
    if (listEl) {
      const qtyLabel = this.dataset.cartQty || 'Antal';
      const decreaseLabel = this.dataset.cartQtyDecrease || 'Minska antal';
      const increaseLabel = this.dataset.cartQtyIncrease || 'Öka antal';
      listEl.innerHTML = this.state.items
        .map((item, index) => {
          const qty = this.itemQty(item);
          const maxQty = this.maxQtyForIndex(index);
          const price = this.linePriceText(item);
          return `<li>
            <div class="sell-console__cart-line">
              <span class="sell-console__cart-name">${this.escapeHtml(item.label)} · ${this.escapeHtml(item.grade || '')}</span>
              <span class="sell-console__cart-price">${this.escapeHtml(price)}</span>
              <div class="sell-console__qty" role="group" aria-label="${this.escapeHtml(qtyLabel)}">
                <button type="button" class="sell-console__qty-btn" data-sell-qty-minus="${index}" aria-label="${this.escapeHtml(decreaseLabel)}" ${qty <= 1 ? 'disabled' : ''}>−</button>
                <input
                  class="sell-console__qty-input"
                  type="number"
                  inputmode="numeric"
                  min="1"
                  max="${maxQty}"
                  step="1"
                  value="${qty}"
                  data-sell-qty="${index}"
                  aria-label="${this.escapeHtml(qtyLabel)}"
                >
                <button type="button" class="sell-console__qty-btn" data-sell-qty-plus="${index}" aria-label="${this.escapeHtml(increaseLabel)}" ${qty >= maxQty ? 'disabled' : ''}>+</button>
              </div>
            </div>
            <button type="button" class="sell-console__cart-remove" data-sell-remove-item="${index}" aria-label="${this.escapeHtml(this.dataset.cartRemove || 'Ta bort')}">×</button>
          </li>`;
        })
        .join('');
    }
    if (totalEl) {
      if (totals.anyManual && !this.state.items.some((i) => i.offeredPrice != null)) {
        totalEl.textContent = this.dataset.manualQuote || 'Manuell värdering';
      } else {
        totalEl.textContent = `${totals.payout} kr`;
      }
    }
    this.querySelectorAll('[data-sell-goto-application]').forEach((btn) => {
      btn.hidden = this.state.items.length === 0;
    });
  }

  ephemeralGameModel(title, platform = '') {
    const offerA = 5;
    const id = `game-temp-${this.normalizeText(title).replace(/\s+/g, '-').slice(0, 60) || 'unknown'}`;
    return {
      id,
      category: 'game',
      platform: platform || 'okänd',
      name: title,
      maxControllers: 0,
      hasDiscDrive: false,
      usedPrice: null,
      offerA,
      source: 'default-low',
      storageOptions: [{ id: 'std', label: 'Standard', basePriceA: offerA }],
    };
  }

  ensureCatalogModel(model) {
    if (!model?.id || !this.state.catalog) return model;
    const exists = (this.state.catalog.models || []).some((m) => m.id === model.id);
    if (!exists) {
      this.state.catalog.models = [...(this.state.catalog.models || []), model];
    }
    return model;
  }

  escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  modelCategory(model, fallback = '') {
    return model?.category || fallback || this.state.category || 'console';
  }

  platformLabel(platform) {
    const hit = (this.state.catalog?.platforms || []).find((p) => p.id === platform);
    return hit?.label || platform || '';
  }

  categoryLabel(category) {
    const labels = {
      console: this.dataset.categoryConsole || 'Konsol',
      accessory: this.dataset.categoryAccessory || 'Konsoltillbehör',
      game: this.dataset.categoryGame || 'Spel',
    };
    return labels[category] || category || '';
  }

  selectedModel() {
    return (this.state.catalog?.models || []).find((m) => m.id === this.state.modelId) || null;
  }

  needsOptions() {
    const model = this.selectedModel();
    const category = this.modelCategory(model, this.state.category);
    if (category === 'game' || category === 'accessory') return false;
    if (this.state.manualEntry) return true;
    if (!model) return false;
    if ((model.storageOptions || []).length > 1) return true;
    if ((Number(model.maxControllers) || 0) > 0) return true;
    const cfg = this.state.catalog?.m2Extra;
    return Boolean(cfg && Array.isArray(cfg.platforms) && cfg.platforms.includes(model.platform));
  }

  stepAfterModel() {
    return this.needsOptions() ? 3 : 4;
  }

  modelsFor(filters = {}, query = '') {
    const models = this.state.catalog?.models || [];
    const q = this.normalizeText(query);
    const category = filters.category || '';
    const platform = filters.platform || '';
    return models.filter((m) => {
      const cat = this.modelCategory(m);
      if (category && cat !== category) return false;
      if (platform && platform !== 'all' && m.platform !== platform) return false;
      if (!q) return true;
      const hay = [m.name, m.id, m.platform, ...(m.aliases || []), ...(m.match || [])].join(' ');
      return this.normalizeText(hay).includes(q);
    });
  }

  mapShopProduct(product, preferredCategory = '') {
    const text = this.normalizeText(`${product.title} ${product.handle}`);
    const shopCategory = this.shopTypeCategory(product);
    const category = preferredCategory || shopCategory || this.state.category || '';
    const models = [...(this.state.catalog?.models || [])].sort((a, b) => {
      const aLen = Math.max(...[a.name, ...(a.match || []), ...(a.aliases || [])].map((s) => String(s).length), 0);
      const bLen = Math.max(...[b.name, ...(b.match || []), ...(b.aliases || [])].map((s) => String(s).length), 0);
      return bLen - aLen;
    });
    for (const model of models) {
      if (category && this.modelCategory(model) !== category) continue;
      const excluded = (model.excludeMatch || []).some((x) => text.includes(this.normalizeText(x)));
      if (excluded) continue;
      const needles = [model.name, ...(model.match || []), ...(model.aliases || [])];
      if (needles.some((n) => n && text.includes(this.normalizeText(n)))) return model;
    }
    return null;
  }

  shopTypeCategory(product) {
    const type = String(product.type || '').toLowerCase();
    const tags = (product.tags || []).join(' ').toLowerCase();
    if (type.includes('spelkonsol') || tags.includes('typ:konsol')) return 'console';
    if (type.includes('tillbehör') || type.includes('tillbehor') || tags.includes('typ:tillbehor')) {
      return 'accessory';
    }
    if (type.includes('spel') || tags.includes('typ:spel')) return 'game';
    return '';
  }

  shopProductCategory(product, mappedModel) {
    return this.shopTypeCategory(product) || (mappedModel ? this.modelCategory(mappedModel) : '');
  }

  isGamingShopProduct(product) {
    const type = String(product.type || '').toLowerCase();
    if (type.includes('mobil') || type.includes('leksak')) return false;
    const tags = (product.tags || []).join(' ').toLowerCase();
    if (tags.includes('typ:telefon') || tags.includes('iphone')) return false;
    if (this.shopProductCategory(product, this.mapShopProduct(product))) return true;
    return type.includes('spel') || type.includes('tillbehör') || type.includes('konsol');
  }

  dedupeShopProducts(products) {
    const seen = new Map();
    for (const product of products || []) {
      const key = String(product.handle || '').replace(/-25-moms$/, '');
      if (!key) continue;
      const existing = seen.get(key);
      if (!existing || String(product.handle).endsWith('-25-moms') === false) {
        seen.set(key, product);
      }
    }
    return [...seen.values()];
  }

  catalogSearchHits(query, limit = 8) {
    const q = this.normalizeText(query);
    if (!q) return [];
    return this.modelsFor({}, query).slice(0, limit);
  }

  async fetchShopProducts(query) {
    const q = String(query || '').trim();
    if (q.length < 2 || !this.shopSearchUrl) return [];
    if (this.searchAbort) this.searchAbort.abort();
    this.searchAbort = new AbortController();
    const url = `${this.shopSearchUrl}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=10`;
    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: this.searchAbort.signal,
      });
      if (!response.ok) return [];
      const data = await response.json();
      const products = data.resources?.results?.products || [];
      return this.dedupeShopProducts(products).filter((p) => this.isGamingShopProduct(p));
    } catch (error) {
      if (error?.name === 'AbortError') return [];
      console.error('[sell-console] shop search failed', error);
      return [];
    }
  }

  onGlobalSearch(value) {
    const results = this.querySelector('[data-sell-search-results]');
    const q = String(value || '').trim();
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (q.length < 2) {
      if (results) {
        results.hidden = true;
        results.innerHTML = '';
      }
      return;
    }
    this.searchTimer = setTimeout(async () => {
      const catalogHits = this.catalogSearchHits(q);
      const shopHits = await this.fetchShopProducts(q);
      this.renderSearchResults(catalogHits, shopHits, q);
    }, 220);
  }

  clearGlobalSearch() {
    const input = this.querySelector('[data-sell-global-search]');
    const results = this.querySelector('[data-sell-search-results]');
    if (input) input.value = '';
    if (results) {
      results.hidden = true;
      results.innerHTML = '';
    }
  }

  renderSearchResults(catalogHits, shopHits, query) {
    const list = this.querySelector('[data-sell-search-results]');
    if (!list) return;
    list.innerHTML = '';
    const usedModels = new Set();
    const items = [];

    for (const product of shopHits) {
      const model = this.mapShopProduct(product);
      if (model) usedModels.add(model.id);
      items.push({
        title: product.title,
        image: product.featured_image?.url || product.image || '',
        category: this.shopProductCategory(product, model) || 'game',
        model,
        shop: product,
      });
    }
    for (const model of catalogHits) {
      if (usedModels.has(model.id)) continue;
      items.push({
        title: model.name,
        image: model.image || '',
        category: this.modelCategory(model),
        model,
        shop: null,
      });
    }

    if (!items.length) {
      list.hidden = false;
      list.innerHTML = `<p class="sell-console__empty">${
        this.dataset.noSearchResults || 'Inga produkter matchade. Välj en kategori eller beskriv produkten.'
      }</p>`;
      return;
    }

    list.hidden = false;
    for (const item of items.slice(0, 10)) {
      list.appendChild(this.createResultButton(item));
    }

    const missing = document.createElement('button');
    missing.type = 'button';
    missing.className = 'sell-console__link-btn';
    missing.textContent = this.dataset.modelMissing || 'Min produkt är inte listad';
    missing.addEventListener('click', () => {
      this.state.category = this.state.category || 'game';
      this.state.modelId = '';
      this.state.manualModel = query;
      this.state.manualEntry = true;
      this.state.fromSearch = true;
      this.showManualModel(true);
      const input = this.querySelector('[name="manualModel"]');
      if (input) input.value = query;
      this.setStep(this.stepAfterModel());
    });
    const wrap = document.createElement('p');
    wrap.className = 'sell-console__hint';
    wrap.appendChild(missing);
    list.appendChild(wrap);
  }

  createResultButton(item) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sell-console__result';
    const meta = [this.categoryLabel(item.category), this.platformLabel(item.model?.platform)]
      .filter(Boolean)
      .join(' · ');
    btn.innerHTML = `
      ${
        item.image
          ? `<img class="sell-console__result-img" src="${this.escapeHtml(item.image)}" alt="" width="64" height="64">`
          : `<span class="sell-console__result-img sell-console__result-img--empty" aria-hidden="true"></span>`
      }
      <span class="sell-console__result-text">
        <strong>${this.escapeHtml(item.title)}</strong>
        <span>${this.escapeHtml(meta)}</span>
      </span>
    `;
    btn.addEventListener('click', () => this.selectSearchItem(item));
    return btn;
  }

  selectSearchItem(item) {
    this.state.fromSearch = true;
    this.state.manualEntry = false;
    this.state.defectIds = [];
    this.state.grade = '';
    this.state.shopProduct = item.shop
      ? {
          title: item.shop.title,
          handle: String(item.shop.handle || '').replace(/-25-moms$/, ''),
          image: item.shop.featured_image?.url || item.shop.image || '',
          type: item.shop.type || '',
        }
      : null;
    this.state.category =
      item.category || this.shopProductCategory(item.shop, item.model) || this.modelCategory(item.model);
    this.state.platform = item.model?.platform || this.state.platform;
    const model =
      item.model && this.modelCategory(item.model) === this.state.category ? item.model : null;
    if (model) {
      this.ensureCatalogModel(model);
      this.state.modelId = model.id;
      this.state.manualModel = '';
      this.showManualModel(false);
      const firstStorage = model.storageOptions?.[0];
      this.state.storageId = firstStorage?.id || '';
      this.state.controllers = Math.min(1, model.maxControllers || 0);
      this.state.controllersDefective = 0;
      this.state.m2ExtraTb = 0;
      this.renderOptions();
      this.setStep(this.stepAfterModel());
      return;
    }
    if (this.state.category === 'game') {
      const temp = this.ephemeralGameModel(item.title, this.detectShopPlatform(item.shop || {}));
      this.ensureCatalogModel(temp);
      this.state.modelId = temp.id;
      this.state.manualModel = item.title;
      this.state.platform = temp.platform;
      this.state.storageId = 'std';
      this.showManualModel(false);
      this.setStep(4);
      return;
    }
    this.state.modelId = '';
    this.state.manualModel = item.title;
    this.showManualModel(true);
    const input = this.querySelector('[name="manualModel"]');
    if (input) input.value = item.title;
    this.setStep(4);
  }

  renderModels(query = '') {
    const list = this.querySelector('[data-sell-models]');
    const heading = this.querySelector('[data-sell-step="2"] h3');
    if (heading) {
      heading.textContent =
        this.state.category === 'game'
          ? this.dataset.chooseGame || 'Sök spelet du vill sälja'
          : this.dataset.chooseModel || 'Välj modell';
    }
    const searchInput = this.querySelector('[data-sell-search]');
    if (searchInput) {
      searchInput.placeholder =
        this.state.category === 'game'
          ? this.dataset.searchGamesPlaceholder || 't.ex. FC 25, Zelda eller GTA'
          : this.dataset.searchPlaceholder || 't.ex. PS5 Slim eller DualSense';
    }
    if (!list || !this.state.catalog) return;

    if (this.state.category === 'game') {
      this.renderGameStep(query);
      return;
    }

    const models = this.modelsFor(
      { category: this.state.category, platform: this.state.platform },
      query
    );
    list.innerHTML = '';
    if (!models.length) {
      list.innerHTML = `<p class="sell-console__empty">${this.dataset.noModels || 'Inga modeller matchade.'}</p>`;
      return;
    }
    for (const model of models) {
      list.appendChild(
        this.createResultButton({
          title: model.name,
          image: model.image || '',
          category: this.modelCategory(model),
          model,
          shop: null,
        })
      );
    }
  }

  async renderGameStep(query = '') {
    const list = this.querySelector('[data-sell-models]');
    if (!list) return;
    const q = String(query || '').trim();
    if (q.length < 2) {
      list.innerHTML = `<p class="sell-console__empty">${
        this.dataset.gameSearchHint || 'Sök på spelets namn, t.ex. FC 25 eller Zelda.'
      }</p>`;
      return;
    }
    list.innerHTML = `<p class="sell-console__hint">${this.dataset.searching || 'Söker…'}</p>`;
    const catalogGames = this.modelsFor({ category: 'game' }, q).slice(0, 12);
    const shopHits = await this.fetchShopProducts(q);
    const shopGames = shopHits.filter((p) => this.shopProductCategory(p, this.mapShopProduct(p, 'game')) === 'game');
    list.innerHTML = '';
    const usedIds = new Set();
    const items = [];

    for (const model of catalogGames) {
      usedIds.add(model.id);
      items.push({
        title: model.name,
        image: model.image || '',
        category: 'game',
        model,
        shop: null,
      });
    }
    for (const product of shopGames) {
      let model = this.mapShopProduct(product, 'game');
      if (model && usedIds.has(model.id)) continue;
      if (!model) {
        model = this.ephemeralGameModel(product.title, this.detectShopPlatform(product));
        this.ensureCatalogModel(model);
      }
      usedIds.add(model.id);
      items.push({
        title: product.title,
        image: product.featured_image?.url || product.image || '',
        category: 'game',
        model,
        shop: product,
      });
    }

    if (!items.length) {
      const temp = this.ephemeralGameModel(q);
      this.ensureCatalogModel(temp);
      list.appendChild(
        this.createResultButton({
          title: q,
          image: '',
          category: 'game',
          model: temp,
          shop: null,
        })
      );
      const hint = document.createElement('p');
      hint.className = 'sell-console__hint';
      hint.textContent = 'Ingen exakt träff – standardbud 5 kr. Vi kan justera efter kontroll.';
      list.appendChild(hint);
      return;
    }
    for (const item of items.slice(0, 15)) {
      list.appendChild(this.createResultButton(item));
    }
  }

  detectShopPlatform(product) {
    const hay = this.normalizeText(`${product?.title || ''} ${product?.handle || ''}`);
    if (hay.includes('ps5') || hay.includes('playstation 5')) return 'ps5';
    if (hay.includes('ps4') || hay.includes('playstation 4')) return 'ps4';
    if (hay.includes('xbox series')) return 'xbox-series';
    if (hay.includes('xbox one')) return 'xbox-one';
    if (hay.includes('xbox 360') || hay.includes('x360')) return 'xbox-360';
    if (hay.includes('switch')) return 'switch';
    return '';
  }

  selectCatalogModel(model) {
    this.state.modelId = model.id;
    this.state.manualModel = '';
    this.state.manualEntry = false;
    this.state.fromSearch = false;
    this.state.shopProduct = null;
    this.state.category = this.modelCategory(model, this.state.category);
    this.state.platform = model.platform || this.state.platform;
    this.showManualModel(false);
    const firstStorage = model.storageOptions?.[0];
    this.state.storageId = firstStorage?.id || '';
    this.state.controllers = Math.min(1, model.maxControllers || 0);
    this.state.controllersDefective = 0;
    this.state.m2ExtraTb = 0;
    this.renderOptions();
    this.setStep(this.stepAfterModel());
  }

  showManualModel(show) {
    const wrap = this.querySelector('[data-sell-manual-wrap]');
    if (wrap) wrap.hidden = !show;
  }

  clampControllers() {
    const model = this.selectedModel();
    const max = model ? Number(model.maxControllers) || 0 : 20;
    let total = Math.max(0, Math.min(max, Number(this.state.controllers) || 0));
    let defective = Math.max(0, Math.min(total, Number(this.state.controllersDefective) || 0));
    this.state.controllers = total;
    this.state.controllersDefective = defective;
  }

  renderOptions() {
    const model = this.selectedModel();
    const storageEl = this.querySelector('[data-sell-storage]');
    const ctrlEl = this.querySelector('[data-sell-controllers]');
    const ctrlHint = this.querySelector('[data-sell-controller-hint]');
    const storageWrap = this.querySelector('[data-sell-storage-wrap]');
    const ctrlWrap = this.querySelector('[data-sell-controllers-wrap]');
    const heading = this.querySelector('[data-sell-step="3"] h3');
    if (heading) {
      heading.textContent = model
        ? this.dataset.chooseOptions || 'Lagring och handkontroller'
        : this.dataset.manualModel || 'Beskriv produkten';
    }

    const category = this.modelCategory(model, this.state.category);
    if (storageWrap) {
      storageWrap.hidden =
        category !== 'console' ||
        !model ||
        !(model.storageOptions || []).length ||
        (model.storageOptions || []).length < 2;
    }
    if (ctrlWrap) {
      ctrlWrap.hidden = category !== 'console' || !model || !(Number(model.maxControllers) > 0);
    }

    if (storageEl) {
      storageEl.innerHTML = '';
      if (model?.storageOptions?.length) {
        for (const opt of model.storageOptions) {
          const label = document.createElement('label');
          label.className = 'sell-console__radio';
          label.innerHTML = `<input type="radio" name="storage" value="${opt.id}" ${
            opt.id === this.state.storageId ? 'checked' : ''
          }><span>${opt.label}</span>`;
          label.querySelector('input').addEventListener('change', () => {
            this.state.storageId = opt.id;
            this.updateQuotePreview();
          });
          storageEl.appendChild(label);
        }
      } else if (this.state.manualModel || !model) {
        storageEl.innerHTML = `<p class="sell-console__hint">${
          this.dataset.manualStorageHint || 'Ange extra detaljer i fritext under kontaktuppgifter om det behövs.'
        }</p>`;
      }
    }

    if (ctrlEl) {
      ctrlEl.innerHTML = '';
      const max = model ? Number(model.maxControllers) || 0 : 20;
      const typeName = model?.controllerType || 'handkontroll';
      const workingPrice = Number(model?.controllerWorking ?? model?.controllerAdd) || 0;
      const defectivePrice = Number(model?.controllerDefective) || 0;

      if (max === 0) {
        this.state.controllers = 0;
        this.state.controllersDefective = 0;
        ctrlEl.innerHTML = `<p class="sell-console__hint">${
          this.dataset.noControllers || 'Inga separata handkontroller för denna modell.'
        }</p>`;
      } else {
        this.clampControllers();
        const totalLabel = this.dataset.controllersTotalLabel || 'Antal {{type}} totalt';
        const defectiveLabel =
          this.dataset.controllersDefectiveLabel ||
          'Varav med stickdrift / defekta knappar';
        const summaryTpl =
          this.dataset.controllersSummary ||
          '{{working}} fungerande (+{{workingPrice}} kr/st), {{defective}} defekta (+{{defectivePrice}} kr/st)';

        ctrlEl.innerHTML = `
          <div class="field">
            <input type="number" class="field__input" name="controllersTotal" min="0" max="${max}" step="1" value="${this.state.controllers}" inputmode="numeric">
            <label class="field__label">${totalLabel.replace('{{type}}', typeName)} (0–${max})</label>
          </div>
          <div class="field">
            <input type="number" class="field__input" name="controllersDefective" min="0" max="${max}" step="1" value="${this.state.controllersDefective}" inputmode="numeric">
            <label class="field__label">${defectiveLabel}</label>
          </div>
          <p class="sell-console__hint" data-sell-controller-summary></p>
        `;

        const totalInput = ctrlEl.querySelector('[name="controllersTotal"]');
        const defectiveInput = ctrlEl.querySelector('[name="controllersDefective"]');
        const summaryEl = ctrlEl.querySelector('[data-sell-controller-summary]');

        const sync = () => {
          this.state.controllers = Number(totalInput.value) || 0;
          this.state.controllersDefective = Number(defectiveInput.value) || 0;
          this.clampControllers();
          totalInput.value = String(this.state.controllers);
          defectiveInput.max = String(this.state.controllers);
          defectiveInput.value = String(this.state.controllersDefective);
          const working = this.state.controllers - this.state.controllersDefective;
          if (summaryEl) {
            summaryEl.textContent = summaryTpl
              .replace('{{working}}', String(working))
              .replace('{{defective}}', String(this.state.controllersDefective))
              .replace('{{workingPrice}}', String(workingPrice))
              .replace('{{defectivePrice}}', String(defectivePrice));
          }
          this.updateQuotePreview();
        };

        totalInput.addEventListener('input', sync);
        defectiveInput.addEventListener('input', sync);
        sync();
      }
    }

    if (ctrlHint) {
      if (model?.controllerType && model.maxControllers > 0) {
        const workingPrice = Number(model.controllerWorking ?? model.controllerAdd) || 0;
        const defectivePrice = Number(model.controllerDefective) || 0;
        ctrlHint.textContent = `${this.dataset.controllerTypeLabel || 'Typ'}: ${model.controllerType} — fungerande ${workingPrice} kr/st, defekt (stickdrift/knappar) ${defectivePrice} kr/st`;
        ctrlHint.hidden = false;
      } else {
        ctrlHint.hidden = true;
      }
    }

    this.renderM2Option(model);
    this.renderDefects();
  }

  renderM2Option(model) {
    const wrap = this.querySelector('[data-sell-m2]');
    if (!wrap) return;
    const cfg = this.state.catalog?.m2Extra;
    const allowed =
      this.modelCategory(model) === 'console' &&
      model &&
      cfg &&
      Array.isArray(cfg.platforms) &&
      cfg.platforms.includes(model.platform);

    if (!allowed) {
      wrap.hidden = true;
      wrap.innerHTML = '';
      this.state.m2ExtraTb = 0;
      return;
    }

    wrap.hidden = false;
    const max = Number(cfg.maxTb) || 8;
    const perTb = Number(cfg.pricePerTb) || 500;
    const label = this.dataset.m2Label || cfg.label || 'Extramonterad M.2 Gen 4';
    const hint = (this.dataset.m2Hint || 'Ange extra TB utöver intern lagring. +{{price}} kr/TB (PCIe Gen 4).').replace(
      '{{price}}',
      String(perTb)
    );

    wrap.innerHTML = `
      <p class="sell-console__hint">${label}</p>
      <p class="sell-console__hint">${hint}</p>
      <div class="field">
        <input type="number" class="field__input" name="m2ExtraTb" min="0" max="${max}" step="1" value="${this.state.m2ExtraTb || 0}" inputmode="numeric">
        <label class="field__label">${this.dataset.m2InputLabel || 'Extra M.2 Gen 4 (TB)'}</label>
      </div>
    `;

    const input = wrap.querySelector('[name="m2ExtraTb"]');
    input.addEventListener('input', () => {
      let n = Math.floor(Number(input.value) || 0);
      n = Math.max(0, Math.min(max, n));
      this.state.m2ExtraTb = n;
      input.value = String(n);
      this.updateQuotePreview();
    });
  }

  renderGrades() {
    const wrap = this.querySelector('[data-sell-grades]');
    if (!wrap || !this.state.catalog) return;
    wrap.innerHTML = '';
    for (const grade of this.state.catalog.grades || []) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sell-console__grade';
      btn.innerHTML = `<strong>${grade.label}</strong><span>${grade.description || ''}</span>`;
      btn.addEventListener('click', () => {
        this.state.grade = grade.id;
        wrap.querySelectorAll('.sell-console__grade').forEach((el) => el.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.renderDefects();
        this.updateQuotePreview();
      });
      if (grade.id === this.state.grade) btn.classList.add('is-selected');
      wrap.appendChild(btn);
    }
  }

  renderDefects() {
    const wrap = this.querySelector('[data-sell-defects]');
    if (!wrap || !this.state.catalog) return;
    const model = this.selectedModel();
    const category = this.modelCategory(model, this.state.category || 'console');
    const allowedIds = new Set();
    wrap.innerHTML = '';
    for (const defect of this.state.catalog.defects || []) {
      const kinds = Array.isArray(defect.kinds) && defect.kinds.length ? defect.kinds : null;
      if (kinds && !kinds.includes(category)) continue;
      if (defect.discOnly && model && !model.hasDiscDrive) continue;
      if (
        Array.isArray(defect.platforms) &&
        defect.platforms.length &&
        model &&
        !defect.platforms.includes(model.platform)
      ) {
        continue;
      }
      if (
        Array.isArray(defect.excludeGrades) &&
        defect.excludeGrades.length &&
        this.state.grade &&
        defect.excludeGrades.includes(this.state.grade)
      ) {
        continue;
      }
      allowedIds.add(defect.id);
      const amount = Number(defect.deduction) || 0;
      const amountLabel =
        amount === 0 ? '' : amount < 0 ? ` +${Math.abs(amount)} kr` : ` −${amount} kr`;
      const label = document.createElement('label');
      label.className = 'sell-console__check';
      const checked = this.state.defectIds.includes(defect.id) ? 'checked' : '';
      label.innerHTML = `<input type="checkbox" value="${defect.id}" ${checked}><span>${defect.label}${amountLabel ? ` <em>${amountLabel.trim()}</em>` : ''}</span>`;
      label.querySelector('input').addEventListener('change', (event) => {
        const id = defect.id;
        if (event.target.checked) {
          if (!this.state.defectIds.includes(id)) this.state.defectIds.push(id);
        } else {
          this.state.defectIds = this.state.defectIds.filter((d) => d !== id);
        }
        this.updateQuotePreview();
      });
      wrap.appendChild(label);
    }
    this.state.defectIds = this.state.defectIds.filter((id) => allowedIds.has(id));
  }

  calculateLocal() {
    const catalog = this.state.catalog;
    if (!catalog) return null;
    if (!this.state.modelId && (this.state.manualModel || this.state.shopProduct)) {
      if (this.state.category === 'game') {
        if (!this.state.grade) return null;
        const mult = Number(catalog.gradeMultipliers?.[this.state.grade] ?? 1);
        return {
          manual: false,
          offeredPrice: Math.max(0, Math.round(5 * mult)),
          quoteValidHours: catalog.quoteValidHours || 24,
        };
      }
      return { manual: true, offeredPrice: null };
    }
    const model = this.selectedModel();
    if (!model || !this.state.grade) return null;
    const storage =
      (model.storageOptions || []).find((s) => s.id === this.state.storageId) ||
      model.storageOptions?.[0];
    if (!storage) return null;
    const override = storage.gradePrices?.[this.state.grade] ?? model.gradePrices?.[this.state.grade];
    const mult = Number(catalog.gradeMultipliers?.[this.state.grade] ?? 1);
    const base =
      override != null && Number.isFinite(Number(override))
        ? Math.round(Number(override))
        : Math.round(Number(storage.basePriceA) * mult);
    const maxCtrl = Number(model.maxControllers) || 0;
    const ctrlTotal = Math.min(Math.max(0, this.state.controllers), maxCtrl);
    const ctrlDefective = Math.min(ctrlTotal, Math.max(0, this.state.controllersDefective || 0));
    const ctrlWorking = ctrlTotal - ctrlDefective;
    const workingPrice = Number(model.controllerWorking ?? model.controllerAdd) || 0;
    const defectivePrice = Number(model.controllerDefective) || 0;
    const controllerAdd = Math.round(ctrlWorking * workingPrice + ctrlDefective * defectivePrice);
    const category = this.modelCategory(model);
    let defectTotal = 0;
    for (const id of this.state.defectIds) {
      const def = (catalog.defects || []).find((d) => d.id === id);
      if (!def) continue;
      const kinds = Array.isArray(def.kinds) && def.kinds.length ? def.kinds : null;
      if (kinds && !kinds.includes(category)) continue;
      if (def.discOnly && !model.hasDiscDrive) continue;
      if (Array.isArray(def.platforms) && def.platforms.length && !def.platforms.includes(model.platform)) {
        continue;
      }
      if (
        Array.isArray(def.excludeGrades) &&
        def.excludeGrades.length &&
        def.excludeGrades.includes(this.state.grade)
      ) {
        continue;
      }
      defectTotal += Number(def.deduction) || 0;
    }
    const consoleMin =
      category === 'console' ? Number(catalog.platformFloors?.[model.platform]?.consoleMin) || 0 : 0;
    const consolePrice = Math.max(consoleMin, base - defectTotal);
    const m2Cfg = catalog.m2Extra || {};
    const m2Allowed =
      category === 'console' && Array.isArray(m2Cfg.platforms) && m2Cfg.platforms.includes(model.platform);
    let m2Tb = m2Allowed ? Math.max(0, Math.min(Number(m2Cfg.maxTb) || 8, Math.floor(this.state.m2ExtraTb || 0))) : 0;
    const m2Add = Math.round(m2Tb * (Number(m2Cfg.pricePerTb) || 0));
    return {
      manual: false,
      offeredPrice: Math.max(0, consolePrice + controllerAdd + m2Add),
      quoteValidHours: catalog.quoteValidHours || 24,
    };
  }

  updateQuotePreview() {
    const quote = this.calculateLocal();
    const cartTotals = this.state.items.length ? this.calculateCartTotals() : null;
    const boxes = this.querySelectorAll('[data-sell-quote]');
    boxes.forEach((box) => {
      const amount = box.querySelector('[data-sell-quote-amount]');
      const note = box.querySelector('[data-sell-quote-note]');
      const isCartSummary = box.hasAttribute('data-sell-quote-cart');
      if (isCartSummary && cartTotals) {
        if (cartTotals.anyManual && !this.state.items.some((i) => i.offeredPrice != null)) {
          if (amount) amount.textContent = this.dataset.manualQuote || 'Manuell värdering';
          if (note) {
            note.textContent =
              this.dataset.manualQuoteNote ||
              'Vi återkommer med pris när vi granskat din ansökan.';
          }
          return;
        }
        if (amount) amount.textContent = `${cartTotals.payout} kr`;
        if (note) {
          const feeNote =
            cartTotals.shippingFee > 0
              ? (
                  this.dataset.shippingFeeNote ||
                  'Brutto {{gross}} kr − frakt {{fee}} kr (under 200 kr).'
                )
                  .replace('{{gross}}', String(cartTotals.gross))
                  .replace('{{fee}}', String(cartTotals.shippingFee))
              : this.dataset.shippingFreeNote || 'Vi står för frakten (bud från 200 kr).';
          const hours = this.state.catalog?.quoteValidHours || 24;
          note.textContent = `${feeNote} ${
            (
              this.dataset.quoteDisclaimer ||
              'Preliminärt bud som gäller i {{hours}} timmar. Slutligt pris efter kontroll.'
            ).replace('{{hours}}', String(hours))
          }`;
        }
        return;
      }
      if (!quote) {
        if (amount) amount.textContent = '–';
        if (note) note.textContent = this.dataset.quoteIncomplete || 'Fyll i stegen för att se bud.';
        return;
      }
      if (quote.manual) {
        if (amount) amount.textContent = this.dataset.manualQuote || 'Manuell värdering';
        if (note) {
          note.textContent =
            this.dataset.manualQuoteNote ||
            'Vi återkommer med pris när vi granskat din ansökan.';
        }
        return;
      }
      if (amount) amount.textContent = `${quote.offeredPrice} kr`;
      if (note) {
        note.textContent = (
          this.dataset.quoteDisclaimer ||
          'Preliminärt bud som gäller i {{hours}} timmar. Slutligt pris efter kontroll.'
        ).replace('{{hours}}', String(quote.quoteValidHours));
      }
    });
    this.renderCart();
  }

  setStep(step) {
    this.state.step = step;
    this.querySelectorAll('[data-sell-step]').forEach((panel) => {
      const n = Number(panel.dataset.sellStep);
      panel.hidden = n !== step;
    });
    this.querySelectorAll('[data-sell-step-indicator]').forEach((el) => {
      const n = Number(el.dataset.sellStepIndicator);
      el.classList.toggle('is-active', n === step);
      el.classList.toggle('is-done', n < step);
    });
    if (step === 1) {
      const browsing =
        !this.state.fromSearch &&
        (this.state.category === 'console' || this.state.category === 'accessory');
      this.showPlatforms(browsing);
    }
    if (step >= 4) this.updateQuotePreview();
    if (step === 3) this.renderOptions();
    if (step === 4) this.renderGrades();
    this.showError('');
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  goBack() {
    const step = this.state.step;
    if (step === 1) {
      if (this.state.category === 'console' || this.state.category === 'accessory') {
        this.state.category = '';
        this.state.platform = '';
        this.showPlatforms(false);
        this.updateStep1Heading();
        return;
      }
      return;
    }
    if (step === 2) {
      this.setStep(1);
      return;
    }
    if (step === 3) {
      if (this.state.fromSearch) {
        this.state.fromSearch = false;
        this.state.category = '';
        this.setStep(1);
        return;
      }
      this.setStep(2);
      return;
    }
    if (step === 4) {
      if (this.needsOptions()) {
        this.setStep(3);
        return;
      }
      if (this.state.fromSearch) {
        this.state.fromSearch = false;
        this.state.category = '';
        this.setStep(1);
        return;
      }
      this.setStep(2);
      return;
    }
    if (step === 5) {
      this.setStep(4);
      return;
    }
    if (step === 6) {
      this.setStep(1);
      return;
    }
    if (step > 1) this.setStep(step - 1);
  }

  goNext() {
    const step = this.state.step;
    if (step === 2 && !this.state.modelId && !this.state.manualModel && !this.state.shopProduct) {
      this.showError(this.dataset.pickModel || 'Välj en modell.');
      return;
    }
    if (step === 3) {
      const manualInput = this.querySelector('[name="manualModel"]');
      if (manualInput && this.querySelector('[data-sell-manual-wrap]')?.hidden === false) {
        this.state.manualModel = String(manualInput.value || '').trim();
        if (!this.state.manualModel && !this.state.modelId) {
          this.showError(this.dataset.pickModel || 'Ange produktnamn.');
          return;
        }
      }
      if (this.state.modelId && !this.state.storageId) {
        const model = this.selectedModel();
        this.state.storageId = model?.storageOptions?.[0]?.id || '';
      }
    }
    if (step === 4 && !this.state.grade) {
      this.showError(this.dataset.pickGrade || 'Välj skick.');
      return;
    }
    if (step === 5) {
      this.addCurrentItemToCart({ goToStep1: true });
      return;
    }
    if (step === 2) {
      this.setStep(this.stepAfterModel());
      return;
    }
    if (step < 6) this.setStep(step + 1);
  }

  showError(message) {
    if (!this.statusEl) return;
    if (!message) {
      this.statusEl.hidden = true;
      this.statusEl.textContent = '';
      return;
    }
    this.statusEl.hidden = false;
    this.statusEl.textContent = message;
  }

  setBusy(isBusy) {
    const btn = this.form?.querySelector('[type="submit"]');
    if (!btn) return;
    btn.disabled = isBusy;
    btn.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  showReceipt(payload) {
    this.state.items = [];
    this.clearPersistedCart();
    this.renderCart();
    if (this.wizard) this.wizard.hidden = true;
    if (this.statusEl) this.statusEl.hidden = true;
    if (!this.receiptEl) return;
    const detail = this.receiptEl.querySelector('[data-sell-detail]');
    if (detail && payload?.message) detail.textContent = payload.message;
    const meta = this.receiptEl.querySelector('[data-sell-application-meta]');
    if (meta && payload?.applicationNumber) {
      const label = meta.dataset.label || 'Ansökningsnummer';
      meta.textContent = `${label}: ${payload.applicationNumber}`;
    }
    this.receiptEl.hidden = false;
    this.receiptEl.focus();
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.apiUrl) {
      this.showError(this.dataset.missingApi || 'API-URL saknas.');
      return;
    }

    if (!this.state.items.length) {
      this.showError(this.dataset.cartEmpty || 'Lägg till minst en produkt först.');
      return;
    }

    const formData = new FormData(this.form);
    const first = this.state.items[0];
    const payload = {
      name: String(formData.get('name') || '').trim(),
      email: String(formData.get('email') || '').trim(),
      phone: String(formData.get('phone') || '').trim(),
      street: String(formData.get('street') || '').trim(),
      zip: String(formData.get('zip') || '').trim(),
      city: String(formData.get('city') || '').trim(),
      serialNumber: '',
      payoutMethod: String(formData.get('payoutMethod') || 'Revolut-länk').trim(),
      payoutDetails: String(formData.get('email') || '').trim(),
      note: String(formData.get('note') || '').trim(),
      acceptTerms: Boolean(formData.get('acceptTerms')),
      items: this.state.items.map((item) => ({
        modelId: item.modelId,
        manualModel: item.manualModel,
        category: item.category,
        shopTitle: item.shopTitle,
        shopHandle: item.shopHandle,
        platform: item.platform,
        storageId: item.storageId,
        controllers: item.controllers,
        controllersDefective: item.controllersDefective,
        m2ExtraTb: item.m2ExtraTb,
        grade: item.grade,
        defectIds: item.defectIds,
        quantity: this.itemQty(item),
      })),
      modelId: first.modelId,
      manualModel: first.manualModel,
      category: first.category,
      shopTitle: first.shopTitle,
      shopHandle: first.shopHandle,
      platform: first.platform,
      storageId: first.storageId,
      controllers: first.controllers,
      controllersDefective: first.controllersDefective,
      m2ExtraTb: first.m2ExtraTb,
      grade: first.grade,
      defectIds: first.defectIds,
    };

    const zipDigits = payload.zip.replace(/\s+/g, '');
    const phoneDigits = payload.phone.replace(/\D/g, '');
    const mobileOk = /^07\d{8}$/.test(phoneDigits) || /^467\d{8}$/.test(phoneDigits);
    if (!payload.street || zipDigits.length !== 5 || !payload.city || !mobileOk) {
      this.showError(this.dataset.addressError || 'Fyll i adress och svenskt mobilnummer.');
      return;
    }
    payload.zip = zipDigits;

    this.setBusy(true);
    this.showError('');

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        this.showError(data.error || this.dataset.genericError || 'Något gick fel.');
        return;
      }
      this.showReceipt(data);
    } catch (error) {
      console.error(error);
      this.showError(this.dataset.networkError || 'Kunde inte nå tjänsten.');
    } finally {
      this.setBusy(false);
    }
  }
}

customElements.define('sell-console-form', SellConsoleForm);
